import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

interface SkillData {
  skill: string;
  score: number;
  category?: string;
}

interface SkillsBarChartProps {
  skills: SkillData[];
  title?: string;
  color?: string;
}

const SkillsBarChart: React.FC<SkillsBarChartProps> = ({
  skills,
  title = 'Technical Skills',
  color = '#10b981'
}) => {
  if (!skills || skills.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-900">{title}</h3>
        <div className="flex items-center justify-center h-64 text-gray-400">
          <p>No skills data available for visualization</p>
        </div>
      </div>
    );
  }

  const sortedSkills = [...skills].sort((a, b) => b.score - a.score);

  const getColorForScore = (score: number) => {
    if (score >= 85) return '#10b981';
    if (score >= 70) return '#0891b2';
    if (score >= 50) return '#f59e0b';
    return '#ef4444';
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
      <h3 className="text-lg font-semibold mb-4 text-gray-900">{title}</h3>
      {skills.length < 3 && (
        <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-700">
            Limited skills data available. For best results, ensure your resume contains detailed skills information.
          </p>
        </div>
      )}
      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={sortedSkills}
          layout="vertical"
          margin={{ top: 5, right: 30, left: 120, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis
            type="number"
            domain={[0, 100]}
            tick={{ fill: '#6b7280', fontSize: 12 }}
            tickFormatter={(value) => `${value}%`}
          />
          <YAxis
            type="category"
            dataKey="skill"
            tick={{ fill: '#374151', fontSize: 12 }}
            width={115}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}
            formatter={(value: number) => [`${value}%`, 'Proficiency']}
            cursor={{ fill: '#f9fafb' }}
          />
          <Bar dataKey="score" radius={[0, 8, 8, 0]}>
            {sortedSkills.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getColorForScore(entry.score)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div className="mt-4 flex items-center justify-center gap-6 text-xs text-gray-600">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          <span>Expert (85-100%)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-cyan-600"></div>
          <span>Advanced (70-84%)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-amber-500"></div>
          <span>Intermediate (50-69%)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <span>Developing (&lt;50%)</span>
        </div>
      </div>
    </div>
  );
};

export default SkillsBarChart;
